import { Fragment } from "react";
import { Checkbox, Text } from "@mantine/core";
import { getColValue as getDisplayValue } from "../../../pages/Reports/Report/Report.utils";
import {
  Flex,
  StyledSpaceCell,
  StyledTable,
  StyledTd,
  StyledTh,
  StyledTr,
} from "../../../styled";
import { ICol, IRow } from "src/interfaces/Logbooks";
import { previewItem } from "./NMEAAmendModal";

const getAllColValues = (row: IRow): any[] => {
  const cols = row.cols ? Object.values(row.cols) : [];
  const subrowCols =
    row.subrows?.flatMap((sr: any) => Object.values(sr.cols ?? {})) ?? [];
  return [...cols, ...subrowCols];
};

const getColValue = (col: ICol): any => {
  let value = col?.value;
  if (typeof col?.value === "object" && col.value !== null) {
    if ("time" in col.value) {
      value = col.value.time;
    } else {
      value = col?.value?.value ?? col?.value ?? "";
    }
  }
  return String(value ?? "");
};

interface Props {
  previewItems: previewItem[];
  selectedRows: Record<string, boolean>;
  onToggleRow: (
    sectionIndex: number,
    rowIndex: number,
    checked: boolean,
  ) => void;
  onToggleSection: (sectionIndex: number, checked: boolean) => void;
}

const NMEAAmendModalPreview = ({
  previewItems,
  selectedRows,
  onToggleRow,
  onToggleSection,
}: Props) => {
  const groupedBySection = previewItems.reduce(
    (acc, item) => {
      const { sectionIndex } = item;
      if (!acc[sectionIndex]) {
        acc[sectionIndex] = [];
      }
      acc[sectionIndex].push(item);
      return acc;
    },
    {} as Record<number, previewItem[]>,
  );

  return (
    <Flex direction="column" gap={24}>
      {Object.entries(groupedBySection).map(([sectionIndex, items]) => {
        const sectionName = items[0]?.sectionName;
        const headers = Object.entries(items[0]?.nmeaRow?.cols || {});
        return (
          <NMEAAmendModalPreviewSection
            key={sectionIndex}
            sectionName={sectionName}
            items={items}
            headers={headers}
            sectionIndex={Number(sectionIndex)}
            selectedRows={selectedRows}
            onToggleRow={onToggleRow}
            onToggleSection={onToggleSection}
          />
        );
      })}
    </Flex>
  );
};

const NMEAAmendModalPreviewSection = ({
  items,
  sectionName,
  headers,
  sectionIndex,
  selectedRows,
  onToggleRow,
  onToggleSection,
}: {
  items: previewItem[];
  sectionName?: string | null;
  headers: [string, ICol][];
  sectionIndex: number;
  selectedRows: Record<string, boolean>;
  onToggleRow: (
    sectionIndex: number,
    rowIndex: number,
    checked: boolean,
  ) => void;
  onToggleSection: (sectionIndex: number, checked: boolean) => void;
}) => {
  const { allSelected, someSelected } = items.reduce(
    (acc, item) => {
      const isSelected = selectedRows[`${item.sectionIndex}-${item.rowIndex}`];
      if (isSelected) {
        acc.allSelected = acc.allSelected && true;
        acc.someSelected = true;
      } else {
        acc.allSelected = false;
      }
      return acc;
    },
    { allSelected: true, someSelected: false },
  );
  return (
    <Flex direction="column" gap={6} style={{ width: "100%" }}>
      {sectionName && (
        <Text fz={13} c="var(--mantine-color-dimmed)" tt="uppercase">
          {sectionName}
        </Text>
      )}
      <StyledTable key={sectionIndex} style={{ border: "none" }}>
        <StyledTr
          style={{
            position: "sticky",
            top: -13,
            zIndex: 1,
          }}>
          {headers.map(([nmeaKey, nmeaCol]) => {
            return (
              <StyledTh key={`header-${nmeaKey}`}>
                {nmeaCol.name?.replaceAll("<br>", " ") ?? nmeaKey}
              </StyledTh>
            );
          })}
          <StyledTh>
            <Flex justify="center" align="center" w="100%" h="100%" px={4}>
              <Checkbox
                size="sm"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={event =>
                  onToggleSection(sectionIndex, event.currentTarget.checked)
                }
              />
            </Flex>
          </StyledTh>
        </StyledTr>

        {items.map((item, i) => (
          <NMEAAmendModalPreviewRow
            key={`${sectionIndex}-${item.rowIndex}`}
            previewIndex={i}
            selected={
              selectedRows[`${item.sectionIndex}-${item.rowIndex}`] ?? false
            }
            onToggleRow={onToggleRow}
            {...item}
          />
        ))}
      </StyledTable>
    </Flex>
  );
};

const NMEAAmendModalPreviewRow = ({
  logbookRow,
  nmeaRow,
  previewIndex,
  sectionIndex,
  rowIndex,
  selected,
  onToggleRow,
}: {
  logbookRow: IRow;
  nmeaRow: previewItem["nmeaRow"];
  previewIndex: number;
  sectionIndex: number;
  rowIndex: number;
  selected: boolean;
  onToggleRow: (
    sectionIndex: number,
    rowIndex: number,
    checked: boolean,
  ) => void;
}) => {
  const nmeaId = nmeaRow.NMEAId;

  const manualColsByNMEAKey: Record<string, any> = {};
  getAllColValues(logbookRow).forEach((col: any) => {
    if (col?.NMEA) {
      manualColsByNMEAKey[col.NMEA] = col;
    }
  });

  const nmeaCols = Object.entries(nmeaRow.cols);

  const comparableCols = nmeaCols.filter(([, col]) => {
    const v = col?.value;
    if (v === null || v === undefined || v === "") return false;
    if (typeof v === "object" && Object.keys(v).length === 0) return false;
    return true;
  });

  const highlightColor = "var(--mantine-color-green-light)";

  return (
    <Fragment>
      {previewIndex !== 0 && (
        <StyledSpaceCell
          colSpan={comparableCols.length}
          style={{ height: 20 }}
        />
      )}

      <StyledTr key={`${nmeaId}-manual`}>
        {comparableCols.map(([nmeaKey]) => {
          const manualCol = manualColsByNMEAKey[nmeaKey];
          const manualValue = getDisplayValue(manualCol);
          return (
            <StyledTd key={`${nmeaId}-manual-${nmeaKey}`}>
              {manualValue}
            </StyledTd>
          );
        })}
        <StyledTd rowSpan={2}>
          <Flex justify="center" align="center" w="100%" h="100%" px={4}>
            <Checkbox
              size="sm"
              checked={selected}
              onChange={event =>
                onToggleRow(sectionIndex, rowIndex, event.currentTarget.checked)
              }
            />
          </Flex>
        </StyledTd>
      </StyledTr>

      <StyledTr key={`${nmeaId}-nmea`}>
        {comparableCols.map(([nmeaKey, nmeaCol]) => {
          const manualCol = manualColsByNMEAKey[nmeaKey];
          const manualValue = getColValue(manualCol ?? "");
          const nmeaValue = getColValue(nmeaCol ?? "");
          const isDifferent = nmeaValue !== manualValue;

          return (
            <StyledTd
              key={`${nmeaId}-nmea-${nmeaKey}`}
              style={
                isDifferent
                  ? {
                      backgroundColor: highlightColor,
                    }
                  : undefined
              }>
              {nmeaValue} {nmeaCol.unit === "24lock" && "LT"}
            </StyledTd>
          );
        })}
      </StyledTr>
    </Fragment>
  );
};

export default NMEAAmendModalPreview;
